/**
 * useWlErrorToast.ts
 * ----------------------------------------------------------------------------
 * Helper centralizado para traduzir erros de RPCs do painel White Label (WL)
 * em mensagens humanas para o usuário final.
 *
 * Contexto de segurança:
 *   - Todas as RPCs `wl_*` validam permissão via `wl_assert_permission` e
 *     levantam `insufficient_privilege` (Postgres errcode 42501) quando o
 *     operador não tem `view/create/edit/delete` no `menu_key` em questão.
 *   - Regras de anti-autoelevação (`wl_save_role_permissions`,
 *     `wl_create_operator`, etc.) usam `RAISE EXCEPTION` com texto humano em
 *     português, então preservamos a mensagem original quando disponível.
 *
 * Uso:
 *   import { describeWlError } from "@/hooks/wl/useWlErrorToast";
 *   toast.error(describeWlError(err));
 */

// Tipagem leve do erro vindo do supabase-js (PostgrestError ou Error nativo).
type WlErrorLike = {
  code?: string;
  message?: string;
  details?: string;
  hint?: string;
};

/**
 * Converte um erro de RPC WL em mensagem amigável.
 * - 42501 / insufficient_privilege  -> "sem permissão" amigável
 * - Mensagens humanas vindas da RPC (RAISE EXCEPTION em PT-BR) são preservadas
 * - Fallback: retorna `message` ou "Erro inesperado."
 */
export function describeWlError(error: unknown): string {
  if (!error) return "Erro inesperado.";

  const e = error as WlErrorLike;
  const code = e?.code ?? "";
  const msg = e?.message ?? "";
  // 1) Rate limit / lockout anti-bruteforce (SQLSTATE 54000)
  if (code === "54000" || msg.includes("program_limit_exceeded") || /Limite de opera|excesso de tentativas/i.test(msg)) {
    return "Você atingiu o limite de operações no painel WL. Aguarde alguns minutos e tente novamente.";
  }

  // 2) Negação de permissão padronizada
  if (code === "42501" || msg.includes("insufficient_privilege")) {
    return "Você não tem permissão para esta ação no painel White Label.";
  }

  // 3) Mensagens humanas já vindas das RPCs (anti-autoelevação, etc.)
  if (msg && /[A-Za-zÀ-ÿ]{4,}/.test(msg)) {
    return msg;
  }

  return msg || "Erro inesperado.";
}

